/**
 * AwsBillAudit.js  —  v34.96 (Feb 13 2026)
 *
 * Focused, zero-friction AWS bill audit. Single page. Drop CSV. See top
 * 5 leaks ranked by $/month. Matches the Landing "What CARTIE can do
 * RIGHT NOW" card's promise exactly.
 *
 * Backend: POST /api/cloud-audit/csv (already exists — auto-detects cost
 * and service columns from any AWS CUR / billing CSV).
 *
 * Why a dedicated page (not just /cloud-audit?provider=aws):
 *   - The Landing card promises a 60-second experience.
 *   - /cloud-audit asks users to pick a provider first (friction).
 *   - This page skips that step entirely.
 */
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Upload, FileText, Loader2, AlertTriangle, TrendingDown, CheckCircle2, ArrowRight, Cloud, Download } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import PublicNavbar from '../components/PublicNavbar';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtUSD = (n) => {
  if (n === null || n === undefined) return '$0';
  if (n < 1) return `$${n.toFixed(2)}`;
  if (n < 1000) return `$${Math.round(n).toLocaleString()}`;
  if (n < 1_000_000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${(n / 1_000_000).toFixed(2)}M`;
};

// ---------------------------------------------------------------------------
// Upload zone
// ---------------------------------------------------------------------------
const UploadZone = ({ onFile, busy, error }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    if (!/\.(csv|json)$/i.test(f.name)) {
      onFile(null, 'Only CSV or JSON files are supported.');
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      onFile(null, 'File too large (>25 MB). Trim your export to one month.');
      return;
    }
    onFile(f, null);
  };

  return (
    <div
      onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
      onClick={() => !busy && inputRef.current?.click()}
      className={`relative rounded-2xl border-2 border-dashed p-10 sm:p-14 text-center transition-all cursor-pointer ${
        busy ? 'border-emerald-500/30 bg-emerald-500/5 cursor-wait' :
        dragging ? 'border-emerald-400 bg-emerald-500/10' :
        'border-white/15 bg-white/[0.02] hover:border-emerald-500/40 hover:bg-white/[0.04]'
      }`}
      data-testid="aws-audit-upload-zone"
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.json,text/csv,application/json"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid="aws-audit-file-input"
      />
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-500/15 ring-1 ring-emerald-500/30 shadow-lg shadow-emerald-500/20 mb-4">
        {busy ? <Loader2 className="w-6 h-6 text-emerald-300 animate-spin" /> : <Upload className="w-6 h-6 text-emerald-300" />}
      </div>
      <h3 className="text-lg font-semibold text-white mb-1">
        {busy ? 'Analyzing your bill…' : 'Drop your AWS billing CSV here'}
      </h3>
      <p className="text-sm text-slate-400 mb-4">
        {busy ? 'This usually takes 5-15 seconds.' : 'or click to browse. CSV & JSON supported. Max 25 MB.'}
      </p>
      {!busy && (
        <div className="text-[11px] text-slate-500 max-w-md mx-auto leading-relaxed">
          Where to get it: <span className="text-emerald-300">AWS Console → Billing → Bills → Download CSV</span>. We process the file in-memory and never store it.
        </div>
      )}
      {error && (
        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-xs" data-testid="aws-audit-error">
          <AlertTriangle className="w-3.5 h-3.5" />
          {error}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------
const Results = ({ data, onReset }) => {
  const top = data.top_services?.slice(0, 5) || [];
  const wins = data.quick_wins || [];

  return (
    <div className="space-y-6" data-testid="aws-audit-results">
      {/* Summary tile */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label="Total monthly spend" value={fmtUSD(data.total_monthly_spend)} testid="aws-audit-total" />
        <Stat label="Estimated waste" value={fmtUSD(data.waste_found)} accent="rose" sub={`~${data.waste_percent}% of bill`} testid="aws-audit-waste" />
        <Stat label="Services scanned" value={String(data.services_found || data.resources_scanned)} accent="slate" testid="aws-audit-services" />
      </div>

      {/* Top 5 leaks — THE thing the Landing card promised */}
      <section data-testid="aws-audit-top-leaks">
        <div className="flex items-center gap-2 mb-3">
          <TrendingDown className="w-4 h-4 text-emerald-300" />
          <h2 className="text-lg font-semibold text-white">Top 5 leaks · ranked by $/month</h2>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] divide-y divide-white/5 overflow-hidden">
          {top.length === 0 && (
            <div className="px-4 py-8 text-center text-slate-400 text-sm">No services found. Check the CSV format.</div>
          )}
          {top.map((s, i) => (
            <div key={s.service + i} className="flex items-center gap-4 px-4 py-3 hover:bg-white/[0.03] transition-colors">
              <span className="text-xs font-mono text-slate-500 w-6 text-right">{i + 1}.</span>
              <span className="text-sm text-white flex-1 truncate" title={s.service}>{s.service}</span>
              <span className="text-sm font-mono text-slate-200 tabular-nums w-24 text-right">{fmtUSD(s.cost)}</span>
              <span className="text-xs text-emerald-300 font-medium tabular-nums w-28 text-right hidden sm:inline">
                ~{fmtUSD(s.cost * 0.25)}/mo savings
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Quick wins */}
      {wins.length > 0 && (
        <section data-testid="aws-audit-quick-wins">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-4 h-4 text-emerald-300" />
            <h2 className="text-lg font-semibold text-white">Quick wins · do these this week</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {wins.map((w, i) => (
              <div key={i} className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                <p className="text-sm font-semibold text-white mb-2">{w.action}</p>
                <div className="flex items-center justify-between text-xs text-slate-300">
                  <span className="font-mono text-emerald-300 tabular-nums">{fmtUSD(w.savings)}/mo</span>
                  <span className="text-slate-500">·</span>
                  <span>{w.effort}</span>
                  <span className="text-slate-500">·</span>
                  <span className={w.risk === 'None' ? 'text-emerald-300' : 'text-amber-300'}>{w.risk} risk</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Footer actions */}
      <div className="flex flex-col sm:flex-row gap-3 pt-4">
        <button
          onClick={onReset}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white font-semibold border border-white/10 transition-colors"
          data-testid="aws-audit-run-again"
        >
          <Upload className="w-4 h-4" />
          Run another bill
        </button>
        <Link
          to="/signup"
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition-colors"
          data-testid="aws-audit-cta-signup"
        >
          Get the full report + auto-fix
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <p className="text-[11px] text-slate-500 text-center pt-2">
        Waste estimate uses the FinOps Foundation 2025 benchmark of ~28% average cloud waste. Your actual savings depend on architecture.
      </p>
    </div>
  );
};

const Stat = ({ label, value, sub, accent = 'emerald', testid }) => {
  const map = {
    emerald: 'text-emerald-300',
    rose: 'text-rose-300',
    slate: 'text-slate-200',
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid={testid}>
      <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1.5">{label}</div>
      <div className={`text-3xl font-bold tabular-nums ${map[accent]}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------
export default function AwsBillAudit() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleFile = async (f, fileError) => {
    if (fileError) { setError(fileError); return; }
    if (!f) return;
    setError(null);
    setFile(f);
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', f);
      form.append('provider', 'aws');
      const res = await axios.post(`${API}/api/cloud-audit/csv`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      setResult(res.data);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Failed to analyze the file.';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg).slice(0, 200));
      setResult(null);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <SEOHead
        title="Audit your AWS bill in 60 seconds · CARTIEAI"
        description="Drop your AWS billing CSV. Get the top 5 cost leaks ranked by $/month plus quick-win actions you can do this week. No signup. No credentials. Free forever."
        path="/aws-bill-audit"
      />
      <PublicNavbar />

      <main className="max-w-4xl mx-auto px-4 pt-24 pb-20">
        {/* Hero */}
        <header className="mb-8 text-center sm:text-left" data-testid="aws-audit-hero">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 mb-4">
            <Cloud className="w-3 h-3 text-emerald-300" />
            <span className="text-[11px] text-emerald-200 font-medium tracking-wide">AWS Bill Audit · zero credentials</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-3">
            Audit your AWS bill in <span className="text-emerald-300">60 seconds</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-300 max-w-2xl">
            Drop your billing CSV. Get the top 5 cost leaks ranked by $/month, plus three quick-win actions you can do this week. Your file is processed in-memory and never stored.
          </p>
        </header>

        {!result && (
          <UploadZone onFile={handleFile} busy={busy} error={error} />
        )}

        {result && (
          <Results data={result} onReset={reset} />
        )}

        {/* What we look for — only shown before upload */}
        {!result && !busy && (
          <section className="mt-12" data-testid="aws-audit-what-we-look-for">
            <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-emerald-400 mb-4">What we look for</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Tile icon={FileText} title="Top spend by service" body="Find the 3-5 services driving 80% of your bill — usually EC2, RDS, and S3 in disguise." />
              <Tile icon={TrendingDown} title="Industry waste benchmark" body="FinOps Foundation 2025 says ~28% of cloud spend is wasted. We show how your bill compares." />
              <Tile icon={CheckCircle2} title="3 quick-win actions" body="Concrete next steps you can do this week — right-sizing, idle resources, reserved-instance gaps." />
            </div>
          </section>
        )}

        {/* Format help — only before upload */}
        {!result && !busy && (
          <section className="mt-10 p-5 rounded-2xl border border-white/10 bg-white/[0.02]" data-testid="aws-audit-format-help">
            <div className="flex items-start gap-3">
              <div className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-cyan-500/10 ring-1 ring-cyan-500/30 flex-shrink-0">
                <Download className="w-4 h-4 text-cyan-300" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white mb-1">Don&apos;t have an AWS CSV handy?</p>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Sign in to your AWS Console → <span className="text-slate-200">Billing & Cost Management</span> → <span className="text-slate-200">Bills</span> → click <span className="text-emerald-300">Download CSV</span> for the current month. Any cost-by-service export works — the parser auto-detects column names.
                </p>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

const Tile = ({ icon: Icon, title, body }) => (
  <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02]">
    <Icon className="w-4 h-4 text-emerald-300 mb-2" />
    <p className="text-sm font-semibold text-white mb-1">{title}</p>
    <p className="text-xs text-slate-400 leading-relaxed">{body}</p>
  </div>
);
